import { storeToRefs } from 'pinia'
import { personBuildFullname } from '@/filters'
import { useUsersStore } from '@/stores/modules/users'
import { useUserStore } from '@/stores/system'
import { getNotificationType } from 'helpers/FormatValue'

interface NotificationContent {
	link?: {
		to?: any
		name: string
	}
}

interface ProcessedNotification {
	id: string
	title: string
	icon: string
	content: NotificationContent & {
		read_at?: string | null
		created_at?: string
	}
}

export function useNotificationProcessor() {
	const usersStore = useUsersStore()
	const { whoami, isAdmin, isTeam } = storeToRefs(useUserStore())

	const personLinkDefault = {
		link: {
			name: 'Keine Information verfügbar',
		},
	}

	async function processNotifications(
		notifications: Api.Notification[]
	): Promise<ProcessedNotification[]> {
		const results = await Promise.allSettled(
			notifications.map((notification) =>
				buildNotificationContent(notification)
			)
		)

		const processed = results
			.filter(
				(result): result is PromiseFulfilledResult<ProcessedNotification> =>
					result.status === 'fulfilled'
			)
			.map((result) => result.value)

		// Log any failed notifications in development
		if (import.meta.env.DEV) {
			const failed = results.filter((result) => result.status === 'rejected')
			if (failed.length > 0) {
				console.warn('Failed to process notifications:', failed)
			}
		}

		return processed
	}

	async function buildNotificationContent(
		notification: Api.Notification
	): Promise<ProcessedNotification> {
		const { icon, title, link } = structuredClone(
			getNotificationType(notification.type).value
		)
		const { id } = notification

		const content = await getContentForNotificationType(notification, link)

		// Special case for ReviewDocumentNotification title
		const finalTitle =
			notification.type === 'App\\Notifications\\ReviewDocumentNotification'
				? notification.data.template.subject
				: title

		return {
			id,
			icon: content.icon || icon,
			title: finalTitle,
			content: {
				...content.content,
				read_at: notification.read_at,
				created_at: notification.created_at,
			},
		}
	}

	async function getContentForNotificationType(
		notification: Api.Notification,
		defaultLink: any
	): Promise<{ content: NotificationContent; icon?: string }> {
		const notificationHandlers: Record<
			string,
			() => Promise<NotificationContent> | NotificationContent
		> = {
			// Person-based notifications
			'App\\Notifications\\Welcome': () => buildPersonLink(notification),
			'App\\Notifications\\LostPassword': () => buildPersonLink(notification),
			'App\\Notifications\\Message': () => buildPersonLink(notification),
			'App\\Notifications\\DamageReport': () => buildPersonLink(notification),
			'App\\Notifications\\ReportNotification': () =>
				buildPersonLink(notification),
			'App\\Notifications\\MeterLevelRejected': () =>
				buildPersonLink(notification),

			// Note-based notifications
			'App\\Notifications\\Notes': () =>
				buildNoteLink(notification, defaultLink),
			'App\\Notifications\\NotesAllUsers': () =>
				buildNoteLink(notification, defaultLink),

			// Simple parameter-based notifications
			'App\\Notifications\\PrivacyTosRejected': () => ({
				link: {
					...defaultLink,
					to: { ...defaultLink.to, params: { id: notification.id } },
				},
			}),

			'App\\Notifications\\TicketHistoryNotification': () => ({
				link: {
					...defaultLink,
					name: `Ticket ${notification.data.ticket.hp_id}`,
					to: {
						...defaultLink.to,
						params: { id: notification.data.ticket.id },
					},
				},
			}),

			'App\\Notifications\\ConsumptionNotification': () => ({
				link: {
					...defaultLink,
					to: {
						...defaultLink.to,
						params: {
							unitId: notification.data.meter.unit_id,
							id: notification.data.meter.building_id,
						},
					},
				},
			}),

			'App\\Notifications\\DocumentUserNotification': () =>
				buildDocumentLink(notification, defaultLink),

			'App\\Notifications\\SyncStatusNotification': () => ({
				link: {
					...defaultLink,
					to: { ...defaultLink.to, params: { id: notification.data.sync_id } },
				},
			}),

			'App\\Notifications\\ReviewDocumentNotification': () => ({
				link: {
					...defaultLink,
					to: {
						...defaultLink.to,
						params: { reviewDocumentId: notification.data.reviewDocument.id },
					},
				},
			}),

			// Default notifications that just use the default link
			'App\\Notifications\\MeterLevelDaily': () => ({ link: defaultLink }),
			'App\\Notifications\\QuotaExceededNotification': () => ({
				link: defaultLink,
			}),
			'App\\Notifications\\HandOverInvitation': () => ({ link: defaultLink }),
			'App\\Notifications\\MessageDone': () => ({ link: defaultLink }),
			'App\\Notifications\\PersonUpdateRequest': () => ({ link: defaultLink }),
			'App\\Notifications\\PersonUpdateRequestAdmin': () => ({
				link: defaultLink,
			}),
		}

		const handler = notificationHandlers[notification.type]

		if (handler) {
			const content = await handler()
			return { content }
		}

		// Fallback for unknown notification types
		return {
			content: { link: defaultLink },
			icon: 'fa-regular fa-question',
		}
	}

	function buildNoteLink(
		notification: Api.Notification,
		defaultLink: any
	): NotificationContent {
		return {
			link: {
				...defaultLink,
				to: {
					...defaultLink.to,
					params: { noteId: notification.data.note.id },
				},
				name: notification.data.note.subject,
			},
		}
	}

	function buildDocumentLink(
		notification: Api.Notification,
		defaultLink: any
	): NotificationContent {
		const {
			documents_updated_yesturday_building = [],
			documents_created_yesturday_building = [],
			documents_created_yesturday_unit = [],
			documents_updated_yesturday_unit = [],
		} = notification.data

		const allIds = [
			...documents_created_yesturday_building,
			...documents_created_yesturday_unit,
			...documents_updated_yesturday_building,
			...documents_updated_yesturday_unit,
		]

		// Remove duplicates
		const uniqueIds = [...new Set(allIds)]
		return {
			link: {
				...defaultLink,
				to: { ...defaultLink.to, query: { ids: uniqueIds } },
			},
		}
	}

	async function buildPersonLink(
		notification: Api.Notification
	): Promise<NotificationContent> {
		// Direct person data available
		if (notification.data.user?.person) {
			return createPersonLink(notification.data.user.person)
		}

		// Admin/Team access to user data
		if (isTeam.value || isAdmin.value) {
			try {
				const userData = await usersStore.show({
					id: notification.data.user.id,
				})
				if (userData?.persons?.length) {
					return createPersonLink(userData.persons[0])
				}
			} catch (error) {
				console.error(
					'Failed to fetch user data for notification:',
					notification.id,
					error
				)
			}
			return personLinkDefault
		}

		// Current user's person data
		const currentUserPerson = whoami.value?.persons?.find(
			(person) => person.user_id === notification.data.user.id
		)

		return currentUserPerson
			? createPersonLink(currentUserPerson)
			: personLinkDefault
	}

	function createPersonLink(person: any): NotificationContent {
		if (!person) return personLinkDefault

		return {
			link: {
				to: {
					name: 'person',
					params: { id: person.id },
				},
				name: personBuildFullname(person),
			},
		}
	}

	return {
		processNotifications,
		buildNotificationContent,
	}
}

// Global type declarations
declare global {
	namespace Api {
		interface Notification {
			id: string
			type: string
			data: any
			read_at?: string | null
			created_at?: string
		}
	}
}
